import { ReactNode } from 'react';

import { isNotFoundError } from '@spotify/confidence-graphql';
import { PermissionUtils } from '@spotify/plugin-confidence-permissions-react';

import { usePageBridgeProps } from '../../../utils';
import { asPageWrapper } from '../../composition';
import { ResourceProvider } from './ResourceContext';
import {
  ResourceErrorPage,
  ResourceLoadingPage,
  ResourceNotFoundErrorPage,
} from './pages';

export type ResourceGuardProps = {
  name?: string;
  label?: string;
  loading?: boolean;
  loadingFallback?: ReactNode;
  error?: Error | null;
  data?: unknown;
};

const BridgedResourceGuard = (
  props: ResourceGuardProps & { children: ReactNode },
) => {
  const { name, label, loadingFallback, error, loading, data, children } =
    props;

  const { descriptor } = usePageBridgeProps();
  const resolvedLabel = label ?? descriptor.title;

  if (error) {
    if (isNotFoundError(error)) {
      return <ResourceNotFoundErrorPage />;
    }

    return (
      <ResourceErrorPage
        title={`Failed to load ${resolvedLabel}`}
        error={`Error loading ${name ?? resolvedLabel}: ${error.message ?? 'Unknown error'}`}
      />
    );
  }

  if (loading) {
    return <>{loadingFallback ?? <ResourceLoadingPage />}</>;
  }

  if (!('data' in props)) {
    return <>{children}</>;
  }

  if (!data) {
    return <ResourceNotFoundErrorPage />;
  }

  const canEdit = PermissionUtils.hasRelation(
    data as Parameters<typeof PermissionUtils.hasRelation>[0],
    'can_edit',
  );
  return (
    <ResourceProvider value={{ data, canEdit }}>{children}</ResourceProvider>
  );
};

export const ResourceGuard = asPageWrapper(
  (_props: ResourceGuardProps): null => null,
  BridgedResourceGuard,
);
